import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { IReceiver } from "dto/IPackage";
import CopyIcon from "assets/icons/copy-new.svg?react";
import AddPresonIcon from "assets/icons/personadd.svg?react";
import { useMemo, useRef, useState } from "react";
import { useOnClickOutside } from "hooks/useClickOutside";
import { IStatus } from "dto/IStatus";
import { EParcelType } from "dto/EParcelType";
import SendStatusBadge from "components/SendStatusBadge";
import statusService from "api/statusService";
import { ICase } from "dto/Cases/ICase";
import casesService from "api/casesService";
import { ICaseParticipant } from "dto/Cases/ICaseParticipant";
import { useTranslation } from "react-i18next";

const copy = (receiver: IReceiver) => {
  navigator.clipboard.writeText(receiver.Email ?? receiver.Name);
};

const Receiver = ({
  receiver,
  addReceiverToCase,
  c,
}: {
  receiver: IReceiver;
  statuses: IStatus[];
  addReceiverToCase: (r: IReceiver) => void;
  c?: ICase;
}) => {
  const { t } = useTranslation();
  const [added, setAdded] = useState<boolean>(
    c?.Participants != null &&
      c.Participants?.find(
        (x) => x.Name === receiver.Name || x.Email === receiver.Email,
      ) != null,
  );

  const addToCase = (receiver: IReceiver) => {
    addReceiverToCase(receiver);
    setAdded(true);
  };

  const getParcelName = () => {
    switch (receiver.ParcelType) {
      case EParcelType.NotaEncrypted:
        return t("outgoingParcelReceiver.notaEncrypted");
      case EParcelType.EDelivery:
      case EParcelType.EncryptedEDelivery:
        return receiver.EDeliveryAddress ?? t("packages.eDelivery");
      case EParcelType.Email:
        if (!receiver.Name || receiver.Name.length == 0) return "";
        return receiver.Email ?? t("outgoingParcelReceiver.emailType");
      case EParcelType.Ebi:
        return t("outgoingParcelReceiver.ebi");
      case EParcelType.TraditionalLetter:
        return t("packages.traditional");
      case EParcelType.ContactMessage:
        return t("packages.encryptedPackage");
    }
  };

  return (
    <div className={styles.box}>
      <div className={styles.info}>
        <div className={styles.name}>
          {receiver.Name && receiver.Name.length > 0
            ? receiver.Name
            : receiver.Email}
        </div>
        <div className={styles.email}>{getParcelName()}</div>
      </div>
      <div className={styles.status}>
        {receiver.SendStatus && (
          <SendStatusBadge
            status={receiver.SendStatus}
            showName={true}
          ></SendStatusBadge>
        )}
      </div>
      {addReceiverToCase && !added && (
        <div className={styles.addperson} onClick={() => addToCase(receiver)}>
          <AddPresonIcon title={t("outgoingParcelReceiver.addToAddressList")}></AddPresonIcon>
        </div>
      )}
      <div className={styles.copy} onClick={() => copy(receiver)}>
        <CopyIcon title={t("common.copy")}></CopyIcon>
      </div>
    </div>
  );
};

const PackageReceiversModal: FC<Props> = ({ pack, cc, toggle, caseId }) => {
  const { data: caseData } = casesService.useGetCase(
    caseId,
    null,
    caseId !== null,
  );
  const { mutate: saveCase } =
    casesService.useCreateCase();
  const ref = useRef(null);

  const { data: statusesData } = statusService.useGetStatuses();

  const statusList = useMemo(() => statusesData?.data ?? [], [statusesData]);

  const handleAddReceiverToCase = (r: IReceiver) => {
    const newParticipant: ICaseParticipant = {
      Id: null,
      Label: r.Name,
      Email: r.Email,
      EDeliveryAddress: r.EDeliveryAddress,
      DefaultParcelType: r.ParcelType,
      RoleId: null,
      IsMain: false,
    };
    caseData.Participants = [newParticipant, ...(caseData.Participants ?? [])];

    saveCase(caseData, {
      onSuccess() {},
    });
  };

  useOnClickOutside(ref, () => {
    toggle();
  });

  return (
    <div ref={ref} className={styles.modal}>
      <div className={styles.body}>
        {pack.Receivers.filter(
          (r) => (!cc && !r.Cc && !r.Bcc) || (cc && (r.Cc || r.Bcc)),
        ).map((r, index) => (
          <>
            <Receiver
              key={index}
              receiver={r}
              statuses={statusList}
              addReceiverToCase={handleAddReceiverToCase}
              c={caseData}
            />
            {pack.Receivers.length - 1 > index && (
              <div className={styles.divinder}></div>
            )}
          </>
        ))}
      </div>
    </div>
  );
};

export default PackageReceiversModal;
